import React, { FC, useState, useEffect } from "react";
import { object, string } from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import { authAPI, UpdateAzureUserRequest, AzureUser } from "../../services/api";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: AzureUser | null;
  onSuccess: () => void;
}

const schema = object({
  email: string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  firstName: string()
    .required("First name is required")
    .max(20, "First name cannot exceed 20 characters")
    .matches(
      /^[A-Za-z\s]*$/,
      "First name cannot contain numbers or special characters"
    ),
  lastName: string()
    .required("Last name is required")
    .max(20, "Last name cannot exceed 20 characters")
    .matches(
      /^[A-Za-z\s]*$/,
      "Last name cannot contain numbers or special characters"
    ),
});

const EditAzureUser: FC<IProps> = ({ open, setOpen, user, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
    },
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const updateData: UpdateAzureUserRequest = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
        };

        const response = await authAPI.updateAzureUser(updateData);
        
        // Show success message
        toast.success(response.message || 'Azure user updated successfully!');
        
        // Close modal
        handleClose();
        
        // Refresh Azure users data
        onSuccess();
        
      } catch (err: any) {
        let errorMessage = 'Failed to update Azure user. Please try again.';
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.status === 400) {
          errorMessage = 'Invalid user data. Please check and try again.';
        } else if (err.response?.status === 404) {
          errorMessage = 'User not found.';
        } else if (err.response?.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    setHasChanges(false);
  };

  // Track changes to enable/disable save button
  useEffect(() => {
    if (user) {
      const originalEmail = user.displayName || "";
      const originalFirstName = user.firstName || "";
      const originalLastName = user.lastName || "";
      
      const hasEmailChanged = formik.values.email !== originalEmail;
      const hasFirstNameChanged = formik.values.firstName !== originalFirstName;
      const hasLastNameChanged = formik.values.lastName !== originalLastName;
      
      setHasChanges(hasEmailChanged || hasFirstNameChanged || hasLastNameChanged);
    }
  }, [formik.values.email, formik.values.firstName, formik.values.lastName, user]);

  useEffect(() => {
    if (user) {
      formik.setValues({
        email: user.displayName || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
      });
      setHasChanges(false);
    }
  }, [user]);

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Edit Azure User
        </Typography>
        <Typography
          variant="body1"
          color="grey"
          className="mt-[5px] font-medium"
        >
          Update the details of the Azure user.
        </Typography>
        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : ""
            }
            onClear={() => formik.setFieldValue("email", "")}
            required
            disabled
            className="!bg-gray-100 hover:cursor-not-allowed"
          />
          <TextField
            label="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.firstName && formik.errors.firstName
                ? formik.errors.firstName
                : ""
            }
            onClear={() => formik.setFieldValue("firstName", "")}
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.lastName && formik.errors.lastName
                ? formik.errors.lastName
                : ""
            }
            onClear={() => formik.setFieldValue("lastName", "")}
            required
          />
          <Button
            label={loading ? "Updating..." : "Update User"}
            variant="primary"
            className="w-full h-[45px] disabled:opacity-200 disabled:cursor-not-allowed disabled:!text-gray-400"
            onClick={() => formik.handleSubmit()}
            loading={loading}
            disabled={!hasChanges || loading}
            type="button"
          />
        </div>
      </div>
    </DialogLayout>
  );
};

export default EditAzureUser;
